import { useContext, useEffect, useState } from "react";
import { tenantContext } from "../Main/contexts";

import { CreateCategory, DeleteCategory, GetCategories, iCategory, iDataColumn, UpdateCategory } from "./TenantAPI";
import { dataColumn } from "../Generic/DataTable";
import { sortByField, SortEnquiriesParams } from "../Logic/sort";
import Loading from "../Generic/loading";
import TextBox from "../Generic/TextBox";
import { lengthIsLessThan } from "../Logic/validationRules";

import TopBar from "./Shared/topBar";
import LeftBar from "./Shared/leftBar";

const COLOUR_OPTIONS = [
    { value: "0", label: "#000000" },
    { value: "32768", label: "#008000" },
    { value: "128", label: "#800000" },
    { value: "255", label: "#FF0000" },
    { value: "65280", label: "#808080" },
    { value: "8421504", label: "#FF0080" },
    { value: "8388863", label: "#FF8000" },
    { value: "33023", label: "#000200" },
    { value: "512", label: "#8000FF" },
    { value: "16711808", label: "#0000FF" },
    { value: "16711680", label: "#0000FF" },
];

function Categories() {
    const { selectedTenant } = useContext(tenantContext);
    const [isLoading, setIsLoading] = useState<Boolean>(true);
    const [categories, setCategories] = useState<iCategory[]>([]);
    const [filteredGridData, setFilteredGridData] = useState<Array<iDataColumn>>([]);
    const [sortBy, setSortBy] = useState<SortEnquiriesParams>();
    const [selectedItem, setSelectedItem] = useState<iDataColumn>({ name: "", id: 0 });
    const [validationErrors, setValidationErrors] = useState<Array<string>>([]);
    const [isNew, setIsNew] = useState<Boolean | undefined>(undefined);
    const [selectedCategory, setSelectedCategory] = useState<iCategory | undefined>(undefined);

    useEffect(() => {
        (async () => {
            await loadCategories();
        })();
    }, [selectedTenant]);

    async function loadCategories() {
        if (selectedTenant) {
            const result = await GetCategories(selectedTenant.token);
            if (result.isOk && result.data) {
                const list = result.data.categories;
                setCategories(list);
                setFilteredGridData(
                    [...list]
                        .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
                        .map(x => ({ id: x.id, name: x.name })),
                );
            }
            setIsLoading(false);
        }
    }

    let AllColumns: dataColumn<iDataColumn>[] = [
        {
            title: <>Categories</>,
            toolTip: "name",
            key: "name",
            getData: (data: iDataColumn) => <>{data.name}</>,
            width: "100%",
            canResize: false,
            sort: {
                direction: undefined,
                sortMethod: () => handleHeaderClick("name"),
                displayIcon: false,
            },
            textAlignment: "left",
            minWidth: "60px",
        },
    ];

    const handleHeaderClick = (fieldName: string): any => {
        let sortO = sortBy;
        if (!sortO) {
            sortO = { orderBy: fieldName, desc: false };
        } else {
            sortO.orderBy = fieldName;
            sortO.desc = !sortO.desc;
        }
        setFilteredGridData(sortByField(filteredGridData, fieldName, sortO.desc));
        setSortBy(sortO);
    };

    const onRowClick = (val: string) => {
        setIsNew(undefined);
        const id = parseInt(val);
        const found = categories.find(x => x.id === id);
        if (found) {
            setSelectedCategory(found);
            setSelectedItem(found);
            setIsNew(false);
        }
    };

    const addCategory = async () => {
        const defCategory: iCategory = { id: 0, name: "", forgroundColour: "#000000" };
        setSelectedItem(defCategory);
        setSelectedCategory(defCategory);
        setIsNew(true);
    };

    const updateCategory = async () => {
        if (selectedTenant && selectedCategory) {
            const colourOption = COLOUR_OPTIONS.find(opt => opt.label === selectedCategory.forgroundColour);
            const categoryToUpdate: iCategory = {
                ...selectedCategory,
                forgroundColour: colourOption ? colourOption.value : selectedCategory.forgroundColour,
            };
            const resp = await UpdateCategory(selectedTenant.token, categoryToUpdate);
            if (resp.isOk) {
                alert("Category updated");
                await loadCategories();
                setIsNew(undefined);
            } else {
                alert("An error occured");
            }
        }
    };

    const deleteCategory = async () => {
        if (selectedTenant) {
            if (selectedItem.id > 0) {
                const confirmDelete = window.confirm("Are you sure you want to delete this Category?");
                if (confirmDelete) {
                    DeleteCategory(selectedTenant.token, selectedItem.id).then(resp => {
                        if (resp.isOk) {
                            alert("Category deleted");
                            loadCategories();
                            setIsNew(undefined);
                        } else if (resp.status === 400 && resp.validationErrors) {
                            alert(Object.values(resp.validationErrors.errors).flat().join("\n"));
                        } else {
                            alert("An error occured");
                        }
                    });
                }
            } else {
                alert("An error occured: no Category selected to delete");
            }
        }
    };

    const createCategory = async () => {
        if (
            selectedCategory &&
            selectedTenant &&
            selectedCategory.name.length > 0 &&
            selectedCategory.name.length < 81
        ) {
            const colourOption = COLOUR_OPTIONS.find(opt => opt.label === selectedCategory.forgroundColour);
            const categoryToCreate: iCategory = {
                ...selectedCategory,
                forgroundColour: colourOption ? colourOption.value : selectedCategory.forgroundColour,
            };
            const resp = await CreateCategory(selectedTenant.token, categoryToCreate);
            if (resp.isOk) {
                alert("Category created");
                await loadCategories();
                setIsNew(undefined);
            } else {
                alert("An error occured");
            }
        } else {
            alert("An error occured. Name is invalid, length has to be between 1 and 80 characters");
        }
    };

    function onFilter(filterValue: string) {
        const filtered = categories
            .map(x => ({ id: x.id, name: x.name }))
            .filter(x => x.name.toLowerCase().includes(filterValue.toLowerCase()));
        setFilteredGridData(filtered);
    }

    const updateValue = (field: string, value: any) => {
        let tCategory = selectedCategory ? JSON.parse(JSON.stringify(selectedCategory)) : {};
        tCategory[field] = value;
        setSelectedCategory(tCategory);
    };

    return (
        <>
            <TopBar
                label={"Category"}
                isNew={isNew}
                updateFunction={updateCategory}
                addFunction={addCategory}
                createFunction={createCategory}
                deleteFunction={deleteCategory}
            ></TopBar>
            <div style={{ display: "flex", flexDirection: "row", height: "calc(100% - 51px)" }}>
                <div className="bodyCol1">
                    {isLoading && <Loading></Loading>}
                    {!isLoading && (
                        <LeftBar
                            AllColumns={AllColumns}
                            gridData={filteredGridData}
                            onRowClick={onRowClick}
                            selectedItem={selectedItem}
                            onFilter={onFilter}
                            onContextMenuClick={() => {}}
                        ></LeftBar>
                    )}
                </div>
                {isNew != undefined && (
                    <div className="bodyCol2">
                        <div className="optionsRow">
                            <p style={{ marginBottom: "6px", marginTop: "-0px", fontWeight: "bold", fontSize: "12pt" }}>
                                {!isNew ? "Edit" : "Create"} Category
                            </p>
                        </div>
                        <div className="formRow">
                            <TextBox
                                id={"name"}
                                label="Name"
                                value={selectedCategory?.name}
                                onChange={(field: string, value: any) => updateValue(field, value)}
                                maxLength={80}
                                requiredIdentifier={true}
                                errorMessage={
                                    validationErrors.find(x => x == "name")
                                        ? "Field must be less than 80 characters"
                                        : ""
                                }
                                validationFunc={lengthIsLessThan}
                            ></TextBox>
                        </div>
                        <div className="formRow">
                            <label style={{ fontSize: "12px", fontWeight: "bold" }}>Foreground Colour</label>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "6px" }}>
                                {COLOUR_OPTIONS.map(opt => (
                                    <label
                                        key={opt.value}
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: "3px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            name="forgroundColour"
                                            value={opt.value}
                                            checked={selectedCategory?.forgroundColour === opt.label}
                                            onChange={() => updateValue("forgroundColour", opt.label)}
                                        />
                                        <div
                                            style={{
                                                width: "60px",
                                                height: "20px",
                                                backgroundColor: opt.label,
                                                border: "1px solid #ccc",
                                            }}
                                        />
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default Categories;
